export type MappedPriceBreakdownDiscount = {
  id?: string;
  name?: string;
  rate_type?: string;
  rate_value?: number;
  total_applied?: number;
  discount_amount?: number;
  num_cycles?: number;
  applies_to_joining_fee_only?: boolean;
  [key: string]: unknown;
};

export type MappedPriceBreakdownTax = {
  id?: string;
  name?: string;
  rate?: number;
  total_applied?: number;
  price?: number;
  [key: string]: unknown;
};

export type MappedPriceBreakdown = {
  is_first_period_free: boolean;
  isProRated: boolean;
  net_total_price: number;
  total_price: number;
  subscription_net_price: number;
  applied_taxes: MappedPriceBreakdownTax[];
  applied_discounts: MappedPriceBreakdownDiscount[];
  applied_discounts_future: MappedPriceBreakdownDiscount[];
  maintenance_fee: Record<string, unknown> | null;
};

type QuoteDiscount = {
  id?: string;
  name?: string;
  type?: string;
  rate?: number;
  total_applied?: number;
  num_cycles?: number;
  applies_to_joining_fee_only?: boolean;
};

type QuoteTax = {
  id?: string;
  name?: string;
  rate?: number;
  total_applied?: number;
};

function emptyMappedBreakdown(): MappedPriceBreakdown {
  return {
    is_first_period_free: false,
    isProRated: false,
    net_total_price: 0,
    total_price: 0,
    subscription_net_price: 0,
    applied_taxes: [],
    applied_discounts: [],
    applied_discounts_future: [],
    maintenance_fee: null,
  };
}

function mapQuoteDiscount(discount: QuoteDiscount): MappedPriceBreakdownDiscount {
  return {
    id: discount.id,
    name: discount.name,
    rate_type: discount.type,
    rate_value: discount.type === 'percentage' ? (discount.rate || 0) * 100 : discount.rate,
    total_applied: discount.total_applied,
    num_cycles: discount.num_cycles,
    applies_to_joining_fee_only: discount.applies_to_joining_fee_only,
  };
}

function mapDueTodayQuote(response: any, mapped: MappedPriceBreakdown): MappedPriceBreakdown {
  const dueToday = response.due_today;
  const lineItem = dueToday.line_items?.[0] || {};
  const membership = lineItem.metadata?.membership || {};
  const futureCharges = Array.isArray(response.future_charges)
    ? response.future_charges
    : [];

  mapped.total_price = dueToday.amount || 0;
  mapped.net_total_price = dueToday.net_price || 0;
  mapped.subscription_net_price = lineItem.amount || 0;
  mapped.isProRated = membership.is_prorated || false;
  mapped.is_first_period_free = membership.free_time_unit_count === 1;
  mapped.maintenance_fee =
    futureCharges.find(
      (charge: { category?: string }) =>
        charge.category === 'RECURRING_CHARGE_MAINTENANCE_FEE'
    ) || null;
  mapped.applied_taxes = (dueToday.taxes || []).map((tax: QuoteTax) => ({
    id: tax.id,
    name: tax.name,
    rate: (tax.rate || 0) * 100,
    total_applied: tax.total_applied,
  }));
  mapped.applied_discounts = (dueToday.discounts || []).map(mapQuoteDiscount);
  if (futureCharges.length > 0) {
    mapped.applied_discounts_future = (
      futureCharges[0].discounts || []
    ).map(mapQuoteDiscount);
  }
  return mapped;
}

function mergeById<T extends { id?: string }>(
  items: T[],
  addAmount: (existing: T, next: T) => void
): T[] {
  return items.reduce((merged: T[], item: T) => {
    const existingIndex = merged.findIndex((entry) => entry.id === item.id);
    if (existingIndex >= 0) {
      addAmount(merged[existingIndex], item);
      return merged;
    }
    merged.push(item);
    return merged;
  }, []);
}

function mapLegacyProductQuote(response: any, mapped: MappedPriceBreakdown): MappedPriceBreakdown {
  const products = response.taxes.products;
  mapped.total_price = products.reduce(
    (sum: number, product: { total_price?: number }) => sum + (product.total_price || 0),
    0
  );
  mapped.applied_taxes = mergeById(
    products.reduce(
      (taxes: MappedPriceBreakdownTax[], product: { applied_taxes?: MappedPriceBreakdownTax[] }) =>
        taxes.concat(product.applied_taxes || []),
      []
    ),
    (existing, next) => {
      existing.price = (existing.price || 0) + (next.price || 0);
    }
  );
  mapped.applied_discounts = mergeById(
    (response.discounts?.products || []).reduce(
      (
        discounts: MappedPriceBreakdownDiscount[],
        product: { applied_discounts?: MappedPriceBreakdownDiscount[] }
      ) => discounts.concat(product.applied_discounts || []),
      []
    ),
    (existing, next) => {
      existing.discount_amount =
        (existing.discount_amount || 0) + (next.discount_amount || 0);
    }
  );
  return mapped;
}

export function mapPriceBreakdownResponse(response?: unknown): MappedPriceBreakdown {
  const mapped = emptyMappedBreakdown();
  if (!response || typeof response !== 'object') {
    return mapped;
  }
  const quote = response as { due_today?: unknown; taxes?: { products?: unknown } };
  if (quote.due_today) {
    return mapDueTodayQuote(quote, mapped);
  }
  if (quote.taxes?.products) {
    return mapLegacyProductQuote(quote, mapped);
  }
  return mapped;
}
